import React from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@revfluence/fresh-icons/regular/esm';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@frontend/shadcn/components/ui/select';
import { cn } from '@frontend/shadcn/lib/utils';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Brief } from '../context/BriefsContext';

interface ProjectGroupProps {
  projectId: number;
  projectName: string;
  briefs: Brief[];
  totalAmount: number;
  totalBriefs: number;
  isExpanded: boolean;
  onToggle: (projectId: number) => void;
  fiscalYear?: string;
  onUpdateState: (updates: { fiscalYear?: string }) => void;
  financialPeriods: Array<{
    key: string;
    id: string;
    label: string;
    granularityLabelForPayload: string;
  }>;
  isSelected: boolean;
  onToggleSelection: () => void;
}

export const ProjectGroupItem: React.FC<ProjectGroupProps> = ({
  projectId,
  projectName,
  briefs,
  totalAmount,
  totalBriefs,
  isExpanded,
  onToggle,
  fiscalYear,
  onUpdateState,
  financialPeriods,
  isSelected,
  onToggleSelection,
}) => {
  const hasFinancialPeriods = financialPeriods?.length > 0;

  return (
    <div className="rounded-xl border border-grey-3 overflow-hidden">
      <div
        className={cn(
          'flex items-center justify-between px-3 py-2 cursor-pointer',
          hasFinancialPeriods ? 'bg-white' : 'bg-grey-1'
        )}
        onClick={() => onToggle(projectId)}
      >
        <div className="flex items-center gap-2">
          <Checkbox
            checked={isSelected}
            disabled={!hasFinancialPeriods}
            className={cn(
              'h-4 w-4 rounded border-gray-300',
              !hasFinancialPeriods && 'opacity-50'
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (hasFinancialPeriods) {
                onToggleSelection();
              }
            }}
          />
          <span className={cn(!hasFinancialPeriods && 'text-grey-5')}>{projectName}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-grey-5">
            {briefs.length}/{totalBriefs} Briefs
          </span>
          {isExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        </div>
      </div>

      {isExpanded && (
        <div className="flex flex-col gap-3 px-3 py-2 border-t border-grey-3">
          <div className="flex justify-between">
            <span className="text-gray-400">Amount</span>
            <div className="bg-white rounded-lg overflow-hidden">
              <Input type="number" className="w-[170px] h-7" value={totalAmount || 0} disabled />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Budget Account</span>
            {hasFinancialPeriods ? (
              <span className="text-sm text-gray-500">Using Project Budget</span>
            ) : (
              <span className="text-sm text-gray-500">No Budget Connected</span>
            )}
          </div>

          {hasFinancialPeriods && (
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Fiscal Year Period</span>
              <Select value={fiscalYear} onValueChange={(value) => onUpdateState({ fiscalYear: value })}>
                <SelectTrigger className="w-[170px] h-7 text-sm">
                  <SelectValue placeholder="Select Fiscal Period" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectGroup>
                    {financialPeriods.map((period) => (
                      <SelectItem key={period.key} value={period.key}>
                        {period.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
